import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI } from '../../services/api';

// Each pending PVC's likely_cause is backend-computed prose (storage_health.py) — shown
// verbatim rather than re-deriving it here, so the UI never drifts from the detection logic.

// The raw provisioner string (e.g. "rancher.io/local-path") names the underlying
// management platform, which nothing in this UI should ever surface directly — same
// rule already applied in ClusterResources.js's isAccessCredentialFailure. Map it to a
// plain description of the storage MECHANISM instead; sc.kind ("csi" | "legacy" |
// "static", from the backend) is enough to describe it without naming the platform.
function describeKind(kind) {
  if (kind === 'csi') return 'Dynamic storage (CSI driver)';
  if (kind === 'legacy') return 'Dynamic storage (local path provisioner)';
  if (kind === 'static') return 'Manually-provisioned volumes';
  return 'Unrecognized storage type';
}

export default function StorageHealth({ clusterId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [installMessage, setInstallMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await clustersAPI.getStorageHealth(clusterId);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to fetch storage health');
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const installProvisioner = async () => {
    setInstalling(true);
    setInstallMessage(null);
    try {
      await clustersAPI.installLocalPathProvisioner(clusterId);
      setInstallMessage({ type: 'ok', text: 'Installed. Re-checking…' });
      await load();
    } catch (e) {
      setInstallMessage({ type: 'err', text: e.response?.data?.detail || 'Install failed' });
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Storage Health</h2>
        <button className="button button-secondary" onClick={load} disabled={loading}
          style={{ padding: '4px 12px', fontSize: 13 }}>
          {loading ? 'Checking...' : '↻ Refresh'}
        </button>
      </div>

      {loading && !data && <div style={{ padding: 16, color: 'var(--muted)' }}>Checking storage classes…</div>}

      {error && (
        <div className="banner danger" style={{ marginTop: 12 }}>{error}</div>
      )}

      {data && (
        <>
          <div style={{ marginTop: 12 }}>
            {data.storage_classes.map(sc => (
              <div key={sc.name} className="info-item" style={{ marginBottom: 10 }}>
                <label>
                  {sc.name}
                  <span style={{
                    marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                    background: sc.healthy === true ? 'var(--color-success-50)'
                      : sc.healthy === false ? 'var(--color-danger-50)' : 'var(--surface-2)',
                    color: sc.healthy === true ? 'var(--color-success-700)'
                      : sc.healthy === false ? 'var(--color-danger-700)' : 'var(--muted)',
                  }}>
                    {sc.healthy === true ? 'HEALTHY' : sc.healthy === false ? 'NOT WORKING' : 'UNKNOWN'}
                  </span>
                </label>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {describeKind(sc.kind)}
                  {sc.reason && <div style={{ color: 'var(--color-danger-700)', marginTop: 4 }}>{sc.reason}</div>}
                </span>
                {sc.fixable_with === 'local_path_provisioner' && (
                  <div style={{ marginTop: 8 }}>
                    <button className="button button-primary" onClick={installProvisioner} disabled={installing}
                      style={{ padding: '4px 12px', fontSize: 12.5 }}>
                      {installing ? 'Installing…' : 'Install storage provisioner'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {installMessage && (
            <div className={installMessage.type === 'ok' ? 'banner success' : 'banner danger'} style={{ marginTop: 4, marginBottom: 12 }}>
              {installMessage.text}
            </div>
          )}

          {data.pending_count > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>
                Pending PersistentVolumeClaims ({data.pending_count})
              </h3>
              <div style={{ border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden' }}>
                {data.pending_pvcs.map((p, i) => (
                  <div key={`${p.namespace}/${p.name}`}
                    style={{ padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                      {p.namespace}/{p.name} <span style={{ color: 'var(--muted)' }}>({p.storage_class || 'no class'})</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{p.likely_cause}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.pending_count === 0 && (
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--color-success-700)' }}>
              No pending PersistentVolumeClaims.
            </div>
          )}
        </>
      )}
    </div>
  );
}
